import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Avatar, Badge, Button, Input, Loading, Rating, Chip } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import ImageUploader from '../../components/ImageUploader';
import VideoUploader from '../../components/VideoUploader';
import CityPicker from '../../components/CityPicker';
import CategoryPicker from '../../components/CategoryPicker';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { requestLocationPermission, getCurrentPosition } from '../../services/location';
import { colors } from '../../theme';
import { APP } from '../../constants';
import { titleCase } from '../../utils/format';
import {
  isFood, isRoad, isRoadType, mixesSides, sideOf, typesOf,
} from '../../utils/partnerTypes';
import VehicleForm from '../../components/VehicleForm';
import { VEHICLE_TYPES, kindsOf } from '../../utils/vertical';
import { PARTNER_TYPES } from '../../constants';

const PartnerProfilePage = () => {
  const navigate = useNavigate();
  const {
    user, partner, setPartner, signOut, setMode,
  } = useAuth();
  const toast = useToast();
  const [pinning, setPinning] = useState(false);

  /** Move the business pin to the browser's position - partners registered before
   *  the location fix were all pinned in Delhi. */
  const pinHere = async () => {
    setPinning(true);
    try {
      const ok = await requestLocationPermission();
      const fix = ok ? await getCurrentPosition() : null;
      if (!fix || !fix.coordinates) { toast.error('Allow location access to pin your business.'); return; }
      const res = await api.partners.updateMe({ coordinates: fix.coordinates });
      setPartner(res.data);
      toast.success('Your business is now pinned at your current location.');
    } catch (e) { toast.error(e.message); } finally { setPinning(false); }
  };

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(partner);
  const [form, setForm] = useState(() => ({
    businessName: (partner && partner.businessName) || '',
    description: (partner && partner.description) || '',
    phone: (partner && partner.phone) || '',
    line1: (partner && partner.address && partner.address.line1) || '',
    city: (partner && partner.address && partner.address.city) || '',
    district: (partner && partner.address && partner.address.district) || '',
    state: (partner && partner.address && partner.address.state) || '',
    pincode: (partner && partner.address && partner.address.pincode) || '',
    deliveryCharge: String((partner && partner.deliveryCharge) || 0),
    minOrderAmount: String((partner && partner.minOrderAmount) || 0),
    deliveryAvailable: Boolean(partner && partner.deliveryAvailable),
    // Showroom facts: the brands on the shelf and what happens after the sale.
    foodType: (partner && partner.foodType) || 'both',
    vehicle: (partner && partner.vehicleDetails) || {},
    brands: ((partner && partner.brands) || []).join(', '),
    sections: (((partner && partner.catalogueSections) || [])).map((s) => s.name),
    newSection: '',
    warrantyInfo: (partner && partner.warrantyInfo) || '',
    installationAvailable: Boolean(partner && partner.installationAvailable),
    autoAcceptOrders: Boolean(partner && partner.autoAcceptOrders),
    logo: (partner && partner.logo) || '',
    coverImage: (partner && partner.coverImage) || '',
    gallery: (partner && partner.gallery) || [],
    video: (partner && partner.video) || '',
    // Menu is stored as [{ title, image, order }]; the uploader works in plain
    // urls, so it is mapped in and out around the picker.
    menu: ((partner && partner.menu) || []).map((m) => m.image).filter(Boolean),
    categories: ((partner && partner.categories) || []).map((c) => String((c && c._id) || c)),
    partnerTypes: typesOf(partner),
    primaryCategory: String((partner && partner.primaryCategory && (partner.primaryCategory._id || partner.primaryCategory)) || ''),
  }));

  const load = useCallback(() => {
    api.partners.me().then((res) => {
      const d = res.data;
      setData(d);
      setPartner(d);
      setForm({
        businessName: d.businessName || '',
        description: d.description || '',
        phone: d.phone || '',
        line1: (d.address && d.address.line1) || '',
        city: (d.address && d.address.city) || '',
        district: (d.address && d.address.district) || '',
        state: (d.address && d.address.state) || '',
        pincode: (d.address && d.address.pincode) || '',
        deliveryCharge: String(d.deliveryCharge || 0),
        minOrderAmount: String(d.minOrderAmount || 0),
        deliveryAvailable: Boolean(d.deliveryAvailable),
        foodType: d.foodType || 'both',
        vehicle: d.vehicleDetails || {},
        brands: (d.brands || []).join(', '),
        sections: (d.catalogueSections || []).map((s) => s.name),
        newSection: '',
        warrantyInfo: d.warrantyInfo || '',
        installationAvailable: Boolean(d.installationAvailable),
        autoAcceptOrders: Boolean(d.autoAcceptOrders),
        logo: d.logo || '',
        coverImage: d.coverImage || '',
        gallery: d.gallery || [],
        video: d.video || '',
        menu: (d.menu || []).map((m) => m.image).filter(Boolean),
        categories: (d.categories || []).map((c) => String((c && c._id) || c)),
        partnerTypes: typesOf(d),
        primaryCategory: String((d.primaryCategory && (d.primaryCategory._id || d.primaryCategory)) || ''),
      });
    }).catch(() => {});
  }, [setPartner]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.partners.updateMe({
        businessName: form.businessName,
        description: form.description,
        phone: form.phone,
        address: {
          ...(data && data.address),
          line1: form.line1,
          city: form.city,
          district: form.district,
          state: form.state,
          pincode: form.pincode,
        },
        deliveryCharge: Number(form.deliveryCharge) || 0,
        minOrderAmount: Number(form.minOrderAmount) || 0,
        deliveryAvailable: form.deliveryAvailable,
        foodType: form.foodType,
        vehicleDetails: isRoad(data || partner) ? form.vehicle : undefined,
        brands: form.brands.split(',').map((b) => b.trim()).filter(Boolean),
        warrantyInfo: form.warrantyInfo,
        installationAvailable: form.installationAvailable,
        autoAcceptOrders: form.autoAcceptOrders,
        logo: form.logo,
        coverImage: form.coverImage,
        gallery: form.gallery,
        video: form.video,
        menu: form.menu.map((image, i) => ({
          title: (data && data.menu && data.menu[i] && data.menu[i].title) || `Page ${i + 1}`,
          image,
          order: i,
        })),
        categories: form.categories,
        partnerTypes: form.partnerTypes,
        primaryCategory: form.primaryCategory || form.categories[0],
      });
      // Sections live on their own endpoint so a rename can carry its items.
      await api.partners.saveSections({ sections: (form.sections || []).map((name) => ({ name, forKind: 'both' })) });
      setData(res.data);
      setPartner(res.data);
      setEditing(false);
      toast.success('Your business profile has been updated.');
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    const ok = await toast.confirm({
      title: 'Log out',
      message: 'Are you sure you want to log out?',
      confirmLabel: 'Log out',
      danger: true,
    });
    if (ok) { await signOut(); navigate('/'); }
  };

  if (!data) return <Loading text="Loading business…" />;

  return (
    <div style={{ maxWidth: 820 }}>
      <PageHeader
        title="My business"
        back={false}
        subtitle={typesOf(data).map(titleCase).join(' · ')}
        right={(
          <Button
            title={editing ? 'Cancel' : 'Edit'}
            variant="light"
            size="sm"
            onClick={() => setEditing((v) => !v)}
          />
        )}
      />

      <Reveal className="card sh-sm" style={{ textAlign: 'center' }}>
        <div style={{ display: 'inline-block' }}>
          <Avatar name={data.businessName} uri={fileUrl(data.logo)} size={76} />
        </div>
        <h3 className="h3" style={{ marginTop: 12 }}>{data.businessName}</h3>
        <div className="t-small">{user && user.name} · +91 {data.phone}</div>
        <div className="row" style={{ justifyContent: 'center', gap: 12, marginTop: 12 }}>
          <Rating value={data.rating} count={data.ratingCount} size={13} />
          <Badge value={data.status} />
          <Badge
            value={data.isOpen ? 'Open' : 'Closed'}
            color={data.isOpen ? colors.success : colors.danger}
          />
        </div>
      </Reveal>

      {editing ? (
        <Reveal className="card sh-sm" style={{ marginTop: 16 }}>
          <form onSubmit={save}>
            <div className="t-tiny" style={{ letterSpacing: 0.6, marginBottom: 12, fontWeight: 800 }}>
              PHOTOS
            </div>
            <ImageUploader
              label="Business logo"
              folder="logo"
              multiple={false}
              size={92}
              value={form.logo}
              onChange={(v) => setForm({ ...form, logo: v })}
              hint="Square works best. Shown next to your name everywhere."
            />
            <ImageUploader
              label="Cover photo"
              folder="cover"
              multiple={false}
              size={92}
              value={form.coverImage}
              onChange={(v) => setForm({ ...form, coverImage: v })}
              hint="A wide shot of your shopfront. Sits at the top of your store page."
            />
            <ImageUploader
              label="Shop photos"
              folder="gallery"
              max={8}
              value={form.gallery}
              onChange={(v) => setForm({ ...form, gallery: v })}
              hint="Inside, outside, your team, your work. Up to 8 photos."
            />
            <VideoUploader
              label="Shop video (optional)"
              hint="A short tour of your shop or your work. Shown on your store page."
              folder="gallery"
              value={form.video}
              onChange={(v) => setForm((f) => ({ ...f, video: v }))}
            />
            <ImageUploader
              label="Menu"
              folder="menu"
              max={8}
              value={form.menu}
              onChange={(v) => setForm({ ...form, menu: v })}
              hint="Photograph each page of your menu or price list. Customers browse these before ordering."
            />

            <div className="t-tiny" style={{ letterSpacing: 0.6, margin: '20px 0 12px', fontWeight: 800 }}>
              DETAILS
            </div>
            <Input
              label="Business name"
              value={form.businessName}
              onChange={(e) => setForm({ ...form, businessName: e.target.value })}
            />
            <div className="t-small" style={{ fontWeight: 700, marginBottom: 4 }}>What you do</div>
            <div className="t-tiny" style={{ marginBottom: 8 }}>Pick every kind of work - your menu and dashboard follow this.</div>
            <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
              {PARTNER_TYPES.map((t) => {
                const on = form.partnerTypes.includes(t.value);
                // Rides and parcels cannot be combined with shop or service work.
                const blocked = !on && mixesSides(form.partnerTypes, t.value);
                return (
                  <Chip
                    key={t.value}
                    label={t.label}
                    icon={t.icon}
                    active={on}
                    style={blocked ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                    onClick={() => {
                      if (blocked) {
                        toast.error(isRoadType(t.value)
                          ? 'Rides and parcels need their own account, separate from shop or service work.'
                          : 'Shop and service work needs its own account, separate from rides and parcels.',
                        'Not on the same account');
                        return;
                      }
                      setForm((f) => {
                        const next = on ? f.partnerTypes.filter((v) => v !== t.value) : [...f.partnerTypes, t.value];
                        if (!next.length) return f;
                        // Switching sides makes the old categories meaningless.
                        return sideOf(next) === sideOf(f.partnerTypes)
                          ? { ...f, partnerTypes: next }
                          : { ...f, partnerTypes: next, categories: [], primaryCategory: '' };
                      });
                    }}
                  />
                );
              })}
            </div>
            <CategoryPicker
              label="Categories you serve"
              value={form.categories}
              primary={form.primaryCategory}
              partnerType={form.partnerTypes.join(',')}
              known={data && data.categories}
              onChange={({ categories, primaryCategory }) => setForm((f) => ({ ...f, categories, primaryCategory }))}
            />
            <Input
              label="About your business"
              textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Input
              label="Contact number"
              inputMode="numeric"
              maxLength={10}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="Address"
              value={form.line1}
              onChange={(e) => setForm({ ...form, line1: e.target.value })}
            />
            <CityPicker
              label="City"
              value={form}
              onChange={({ city, district, state }) => setForm({ ...form, city, district, state })}
            />
            <Input
              label="Pincode"
              inputMode="numeric"
              maxLength={6}
              value={form.pincode}
              onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '') })}
            />

            <div className="form-row">
              <Input
                label="Delivery charge (₹)"
                className="grow"
                inputMode="numeric"
                value={form.deliveryCharge}
                onChange={(e) => setForm({ ...form, deliveryCharge: e.target.value.replace(/\D/g, '') })}
              />
              <Input
                label="Min order (₹)"
                className="grow"
                inputMode="numeric"
                value={form.minOrderAmount}
                onChange={(e) => setForm({ ...form, minOrderAmount: e.target.value.replace(/\D/g, '') })}
              />
            </div>

            {isRoad(data || partner) ? (
              <>
                <div className="t-title" style={{ marginTop: 14 }}>Your vehicle</div>
                <div className="t-tiny" style={{ marginBottom: 8 }}>Shown to admin and to the customer you are picking up.</div>
                <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                  {VEHICLE_TYPES.map((t) => (
                    <Chip key={t.value} label={t.label} active={form.vehicle.type === t.value}
                      onClick={() => setForm({ ...form, vehicle: { ...form.vehicle, type: t.value, wheels: t.wheels } })} />
                  ))}
                </div>
                <VehicleForm type={form.vehicle.type} value={form.vehicle} onChange={(vehicle) => setForm({ ...form, vehicle })} />
              </>
            ) : null}
            {isFood(data || partner) ? (
              <>
                <label className="field-label">What does your kitchen serve?</label>
                <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                  {[['veg', '🟢 Pure veg'], ['non_veg', '🔴 Non-veg'], ['both', '🟢🔴 Both']].map(([v, l]) => (
                    <Chip key={v} label={l} active={form.foodType === v} onClick={() => setForm({ ...form, foodType: v })} />
                  ))}
                </div>
              </>
            ) : null}
            {/* The partner's own way of grouping their catalogue. */}
            <label className="field-label">Your own categories</label>
            <div className="t-tiny" style={{ marginBottom: 6 }}>
              Your shop&apos;s own grouping - &quot;Winter wear&quot;, &quot;AC repairs&quot;. Customers browse and filter by these; they stay inside your business.
            </div>
            <div className="row wrap" style={{ gap: 8, marginBottom: 8 }}>
              {(form.sections || []).map((sec) => (
                <Chip key={sec} label={`${sec}  ✕`} onClick={() => setForm({ ...form, sections: form.sections.filter((x) => x !== sec) })} />
              ))}
            </div>
            <div className="row" style={{ gap: 8, alignItems: 'flex-start', marginBottom: 14 }}>
              <Input
                placeholder="Add a category of your own"
                className="grow"
                value={form.newSection}
                onChange={(e) => setForm({ ...form, newSection: e.target.value })}
                style={{ marginBottom: 0 }}
              />
              <Button
                title="Add"
                variant="light"
                onClick={() => {
                  const name = (form.newSection || '').trim();
                  if (!name || form.sections.includes(name)) return;
                  setForm({ ...form, sections: [...form.sections, name], newSection: '' });
                }}
              />
            </div>

            <Input
              label="Brands you stock"
              placeholder="LG, Samsung, Voltas, Daikin"
              hint="Separate brands with commas. Customers see these on your page."
              value={form.brands}
              onChange={(e) => setForm({ ...form, brands: e.target.value })}
            />
            <Input
              label="Warranty & after-sales"
              placeholder="e.g. Brand warranty, free installation within 5 km"
              textarea
              rows={3}
              value={form.warrantyInfo}
              onChange={(e) => setForm({ ...form, warrantyInfo: e.target.value })}
            />

            {[
              {
                key: 'installationAvailable', on: '🔧', off: '🔩',
                title: 'Installation',
                text: form.installationAvailable
                  ? 'You install what you sell.'
                  : 'Customers arrange their own installation.',
              },
              {
                key: 'deliveryAvailable', on: '🛵', off: '🏪',
                title: form.deliveryAvailable ? 'Delivery available' : 'Delivery not available',
                text: form.deliveryAvailable
                  ? 'Customers can add to cart and order. A rider is sent when you mark an order ready.'
                  : 'Customers can browse your products and reviews, but cannot order.',
              },
              {
                key: 'autoAcceptOrders', on: '⚡', off: '🖐️',
                title: form.autoAcceptOrders ? 'Auto-accept orders' : 'Accept orders manually',
                text: form.autoAcceptOrders
                  ? 'New orders are accepted for you while you are within your opening hours. Outside them they wait.'
                  : 'Each new order waits until you click Accept.',
              },
            ].map((t) => (
              <label key={t.key} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14,
                border: `1.4px solid ${form[t.key] ? colors.success : colors.line}`,
                background: form[t.key] ? colors.successBg : '#fff', marginBottom: 10, cursor: 'pointer',
              }}>
                <span style={{ fontSize: 20 }}>{form[t.key] ? t.on : t.off}</span>
                <span className="grow">
                  <span className="t-title" style={{ display: 'block' }}>{t.title}</span>
                  <span className="t-tiny">{t.text}</span>
                </span>
                <input type="checkbox" checked={form[t.key]}
                  onChange={(e) => setForm({ ...form, [t.key]: e.target.checked })}
                  style={{ width: 20, height: 20, accentColor: colors.success }} />
              </label>
            ))}

            <Button title="Save changes" type="submit" loading={saving} size="lg" full />
          </form>
        </Reveal>
      ) : (
        <Reveal delay={70} className="card sh-sm" style={{ marginTop: 16 }}>
          {[
            ['What you do', typesOf(data).map(titleCase).join(', ')],
            ['About', data.description],
            ['Phone', data.phone],
            ['Address', [
              data.address && data.address.line1,
              data.address && data.address.city,
              data.address && data.address.district,
              data.address && data.address.state,
              data.address && data.address.pincode,
            ].filter(Boolean).join(', ')],
            ['Categories', ((data.categories) || []).map((c) => c.name || c).join(', ')],
            ['Delivery charge', `₹${data.deliveryCharge || 0}`],
            ['Minimum order', `₹${data.minOrderAmount || 0}`],
            ['Total orders', String(data.totalOrders || 0)],
            ['Total bookings', String(data.totalBookings || 0)],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} className="row" style={{ padding: '8px 0', gap: 12, alignItems: 'flex-start' }}>
              <span className="t-small" style={{ width: 140, flex: '0 0 140px' }}>{label}</span>
              <span className="t-body grow" style={{ fontWeight: 600 }}>{value}</span>
            </div>
          ))}
        </Reveal>
      )}

      <div className="col" style={{ gap: 12, marginTop: 20 }}>
        {data && data._id ? (
          <Button
            title="View my store as a customer"
            icon="👀"
            variant="ghost"
            full
            onClick={() => navigate(`/business/${data._id}?preview=1`)}
          />
        ) : null}
        {/*
          * Everyone who earns through KAAMWALA needs somewhere to be paid,
          * so this sits above the work-specific rows, not inside one branch.
          */}
        <Button
          title="Bank details for settlements"
          icon="🏦"
          variant="ghost"
          full
          onClick={() => navigate('/partner/bank')}
        />
        {isRoad(data) ? (
          <>
            {/* Riders do not hire - they look for work themselves. */}
            <Button title="Find a job" icon="💼" variant="ghost" full onClick={() => navigate('/jobs')} />
            <Button title="My job applications" icon="📄" variant="ghost" full onClick={() => navigate('/my-applications')} />
          </>
        ) : (
          <>
            <Button
              title="My jobs & applicants"
              icon="💼"
              variant="ghost"
              full
              onClick={() => navigate('/partner/jobs')}
            />
            <Button
              title="Job seekers"
              icon="🔎"
              variant="ghost"
              full
              onClick={() => navigate('/people')}
            />
          </>
        )}
        {kindsOf(data || partner).includes('garage') ? (
          <Button
            title="Service jobs (garage)"
            icon="🔧"
            variant="ghost"
            full
            onClick={() => navigate('/partner/service-jobs')}
          />
        ) : null}
        <Button
          title="My hotel & rooms"
          icon="🏨"
          variant="ghost"
          full
          onClick={() => navigate('/partner/hotel')}
        />
        <Button
          title="360° showroom tour"
          icon="🎥"
          variant="ghost"
          full
          onClick={() => navigate('/partner/showroom-tour')}
        />
        <Button
          title="Job seeker profile"
          icon="🧑‍🔧"
          variant="ghost"
          full
          onClick={() => navigate('/work-profile')}
        />
        <Button
          title="Pin my business to my current location"
          icon="📍"
          variant="light"
          full
          loading={pinning}
          onClick={pinHere}
        />
        <Button
          title="Switch to customer view"
          icon="🙋"
          variant="light"
          full
          onClick={() => { setMode('user'); navigate('/home'); }}
        />
        <Button
          title="Help & support"
          icon="🎧"
          variant="light"
          full
          onClick={() => navigate('/support')}
        />
        <Button
          title="Call us"
          icon="📞"
          variant="light"
          full
          onClick={() => { window.location.href = `tel:${APP.supportPhone}`; }}
        />
        <Button title="Log out" variant="danger" full onClick={logout} />
      </div>

      <div className="t-tiny" style={{ textAlign: 'center', marginTop: 20 }}>
        {APP.name} Partner · v1.0.0
      </div>
    </div>
  );
};

export default PartnerProfilePage;
